import React, { useState, useEffect } from 'react';
import { getOrders, updateOrderStatus, buyShippingLabel } from '../services/api';
import './AdminOrders.css';

const STATUS_OPTS = ['awaiting_payment', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];
const STATUS_BADGES = {
  awaiting_payment: 'badge-warning',
  processing: 'badge-info',
  shipped: 'badge-info',
  delivered: 'badge-success',
  cancelled: 'badge-danger',
  refunded: 'badge-gray',
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [buyingLabel, setBuyingLabel] = useState(false);
  const [labelError, setLabelError] = useState('');

  const load = () => {
    setLoading(true);
    getOrders({ status: statusFilter, search })
      .then(r => setOrders(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter, search]);

  const handleStatusChange = async (orderId, newStatus) => {
    await updateOrderStatus(orderId, newStatus);
    load();
    if (selectedOrder?.id === orderId) {
      setSelectedOrder(prev => ({ ...prev, status: newStatus }));
    }
  };

  const handleBuyLabel = async (order) => {
    if (!order.easypost_shipment_id) {
      setLabelError('No shipment ID found. Customer must re-checkout to generate a new shipment.');
      return;
    }
    setBuyingLabel(true);
    setLabelError('');
    try {
      // For demo: you'd need the rateId stored too. This assumes it's on the order.
      const res = await buyShippingLabel({
        orderId: order.id,
        rateId: order.selected_rate_id,
        shipmentId: order.easypost_shipment_id,
      });
      alert(`✅ Label purchased!\nTracking: ${res.data.trackingNumber}\nCarrier: ${res.data.carrier}`);
      load();
    } catch (err) {
      setLabelError(err.response?.data?.error || 'Label purchase failed.');
    } finally {
      setBuyingLabel(false);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <div className="page-header"><h1>Orders</h1><p>Manage orders, ship items, print labels.</p></div>

      {/* Filters */}
      <div className="orders-filters">
        <input
          className="form-control"
          placeholder="Search by order #, email, or tracking..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: '320px' }}
        />
        <select className="form-control" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All Statuses</option>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      <div className="orders-layout">
        {/* List */}
        <div className="orders-list">
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : orders.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-500)' }}>No orders found.</div>
          ) : orders.map(order => (
            <div
              key={order.id}
              className={`order-row card ${selectedOrder?.id === order.id ? 'selected' : ''}`}
              onClick={() => setSelectedOrder(order)}
            >
              <div className="order-row-top">
                <code className="order-num">{order.order_number}</code>
                <span className={`badge ${STATUS_BADGES[order.status] || 'badge-gray'}`}>
                  {order.status?.replace(/_/g, ' ')}
                </span>
              </div>
              <div className="order-row-bottom">
                <span>{order.customer_name}</span>
                <span>${order.total?.toFixed(2)}</span>
                <span style={{ color: 'var(--gray-500)', fontSize: '0.78rem' }}>
                  {new Date(order.created_at).toLocaleDateString()}
                </span>
              </div>
              {order.tracking_number && (
                <div className="order-row-tracking">📦 {order.carrier} — {order.tracking_number}</div>
              )}
            </div>
          ))}
        </div>

        {/* Detail panel */}
        {selectedOrder ? (
          <div className="order-detail card">
            <h2 style={{ fontSize: '1rem', marginBottom: '1.25rem' }}>Order Detail</h2>

            <div className="detail-section">
              <label className="detail-label">Order #</label>
              <p><code>{selectedOrder.order_number}</code></p>
            </div>

            <div className="detail-section">
              <label className="detail-label">Customer</label>
              <p>{selectedOrder.customer_name}</p>
              <p>{selectedOrder.customer_email}</p>
            </div>

            <div className="detail-section">
              <label className="detail-label">Ship To</label>
              <p>{selectedOrder.shipping_street}</p>
              <p>{selectedOrder.shipping_city}, {selectedOrder.shipping_state} {selectedOrder.shipping_zip}</p>
            </div>

            <div className="detail-section">
              <label className="detail-label">Items</label>
              {(selectedOrder.items || []).map((item, i) => (
                <p key={i}>{item.title} × {item.qty} — ${(item.price * item.qty).toFixed(2)}</p>
              ))}
              <p style={{ fontWeight: 700, marginTop: '0.3rem' }}>Total: ${selectedOrder.total?.toFixed(2)}</p>
            </div>

            <div className="detail-section">
              <label className="detail-label">Payment</label>
              <p>{selectedOrder.payment_method?.toUpperCase()} — {selectedOrder.payment_status}</p>
            </div>

            {selectedOrder.tracking_number && (
              <div className="detail-section">
                <label className="detail-label">Tracking</label>
                <p>{selectedOrder.carrier}: <strong>{selectedOrder.tracking_number}</strong></p>
                {selectedOrder.label_url && (
                  <a href={selectedOrder.label_url} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm" style={{ marginTop: '0.4rem' }}>
                    🖨️ Print Label
                  </a>
                )}
              </div>
            )}

            {/* Status update */}
            <div className="detail-section">
              <label className="detail-label">Update Status</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {STATUS_OPTS.map(s => (
                  <button
                    key={s}
                    className={`btn btn-sm ${selectedOrder.status === s ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handleStatusChange(selectedOrder.id, s)}
                  >
                    {s.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Buy label */}
            {!selectedOrder.tracking_number && selectedOrder.payment_status === 'paid' && (
              <div className="detail-section">
                <label className="detail-label">Shipping Label</label>
                {labelError && <div className="alert alert-error" style={{ marginBottom: '0.5rem' }}>{labelError}</div>}
                <button
                  className="btn btn-success"
                  onClick={() => handleBuyLabel(selectedOrder)}
                  disabled={buyingLabel}
                >
                  {buyingLabel ? '...' : '🏷️ Buy Shipping Label'}
                </button>
                <p style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginTop: '0.4rem' }}>
                  Uses the carrier/rate selected during checkout.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="card order-detail-empty">
            <p>Select an order to view details.</p>
          </div>
        )}
      </div>
    </div>
  );
}
